import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submenu", "meButton", "meBadge", "secondary", "stack", "axleOverlay", "axleMessages", "axleInput", "axleButton", "axleCount", "axleStatus"]
  static values = { userId: Number }

  connect() {
    this.longPressTimer = null
    this.longPressTriggered = false
    this.activeSubmenuIndex = null
    this.secondaryOpen = false

    this._onDocumentClick = this._dismissSubmenu.bind(this)
    this._onOutsideClose = this._handleOutsideClose.bind(this)
    this._onVvResize = this._updateAxleViewport.bind(this)
    this._onTouchMoveLock = this._lockTouchMove.bind(this)

    document.addEventListener("click", this._onDocumentClick, true)
    document.addEventListener("click", this._onOutsideClose, true)

    this._highlightActive()
    this._renderAxleMessages()
    this._refreshUnreadCount()
    this._installLocationShareHook()
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick, true)
    document.removeEventListener("click", this._onOutsideClose, true)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this._onVvResize)
      window.visualViewport.removeEventListener("scroll", this._onVvResize)
    }
    document.removeEventListener("touchmove", this._onTouchMoveLock, { passive: false })
    this._unlockPageScroll()
    this._clearTimer()
    this._removeLocationShareHook()
  }

  toggleSecondary(event) {
    event.preventDefault(); event.stopPropagation()
    this._dismissSubmenu()
    this.closeAxleOverlay()
    if (!this.hasSecondaryTarget) return
    this.secondaryOpen = !this.secondaryOpen
    this.secondaryTarget.classList.toggle("dock-secondary-open", this.secondaryOpen)
    if (this.hasMeButtonTarget) this.meButtonTarget.classList.toggle("dock-active", this.secondaryOpen)
  }

  closeSecondary() {
    if (!this.hasSecondaryTarget) return
    this.secondaryOpen = false
    this.secondaryTarget.classList.remove("dock-secondary-open")
    if (this.hasMeButtonTarget) this.meButtonTarget.classList.remove("dock-active")
  }

  openAxleOverlay(event) {
    event.preventDefault(); event.stopPropagation()
    if (!this.hasAxleOverlayTarget) return
    this.element.classList.add("axle-overlay-open")
    this.axleOverlayTarget.classList.remove("hidden")
    this._lockPageScroll()

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this._onVvResize)
      window.visualViewport.addEventListener("scroll", this._onVvResize)
    }
    document.addEventListener("touchmove", this._onTouchMoveLock, { passive: false })

    this._setAxleStatus("ready")
    this._renderAxleMessages()
    this._updateAxleViewport()

    this.axleInputTarget?.focus({ preventScroll: true })
    requestAnimationFrame(() => {
      this.axleInputTarget?.focus({ preventScroll: true })
      this._updateAxleViewport()
    })

    localStorage.setItem(this._axleLastReadKey(), String(Date.now()))
    this._refreshUnreadCount()
  }

  closeAxleOverlay(event) {
    event?.preventDefault()
    if (!this.hasAxleOverlayTarget || this.axleOverlayTarget.classList.contains("hidden")) return
    this.axleOverlayTarget.classList.add("hidden")
    this.element.classList.remove("axle-overlay-open")
    this.axleInputTarget?.blur()
    this.axleOverlayTarget.style.top = ""
    this.axleOverlayTarget.style.height = ""
    this._unlockPageScroll()

    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this._onVvResize)
      window.visualViewport.removeEventListener("scroll", this._onVvResize)
    }
    document.removeEventListener("touchmove", this._onTouchMoveLock, { passive: false })
  }

  async sendAxleOverlay(event) {
    event.preventDefault()
    const message = this.axleInputTarget?.value?.trim()
    if (!message) return
    this._pushAxleMsg("user", message)
    this.axleInputTarget.value = ""
    this._setAxleStatus("thinking")

    try {
      const token = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
      let response = await fetch("/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ message })
      })

      // Backward-compatible fallback for older deployments
      if (response.status === 404) {
        response = await fetch("/axle_overlay", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
          body: JSON.stringify({ message })
        })
      }

      const data = await response.json()
      const last = data?.messages?.[data.messages.length - 1]
      const rawAssistant = last?.content || data?.response || data?.error || "Done"
      this._pushAxleMsg("assistant", this._normalizeAssistantText(rawAssistant), last?.formatted_content)

      // Inline provider previews (when backend returns nearby org cards)
      const orgCards = data?.organizations?.html
      if (Array.isArray(orgCards) && orgCards.length > 0) {
        const cardsHtml = `<div class="axle-inline-cards">${orgCards.slice(0, 3).join("")}</div>`
        this._pushAxleMsg("assistant", "Nearby providers", cardsHtml)
      }

      this._setAxleStatus(data?.success ? "ready" : "error")
      this._refreshUnreadCount()
    } catch (_e) {
      this._pushAxleMsg("assistant", "Connection issue. Try again.")
      this._setAxleStatus("error")
      this._refreshUnreadCount()
    }
  }

  longPressStart(event) {
    const el = event.currentTarget
    this.longPressTriggered = false
    el.classList.add("dock-pressing")
    this.longPressTimer = setTimeout(() => {
      this.longPressTriggered = true
      el.classList.remove("dock-pressing")
      el.classList.add("dock-submenu-active")
      this._showSubmenu(el.dataset.submenuIndex, el)
      if (navigator.vibrate) navigator.vibrate(10)
    }, 500)
  }

  longPressEnd(event) {
    const el = event.currentTarget
    el.classList.remove("dock-pressing")
    if (this.longPressTriggered) event.preventDefault(); else this._clearTimer()
    el.classList.remove("dock-submenu-active")
  }

  longPressCancel(event) { event.currentTarget.classList.remove("dock-pressing", "dock-submenu-active"); this._clearTimer() }
  preventContext(event) { event.preventDefault() }

  _setAxleStatus(state) {
    if (!this.hasAxleStatusTarget) return
    this.axleStatusTarget.textContent = ({ ready: "🟢", thinking: "🟡", error: "🔴" })[state] || "🟢"
  }

  _refreshUnreadCount() {
    if (!this.hasAxleCountTarget) return
    const msgs = this._axleMsgs()
    const lastRead = parseInt(localStorage.getItem(this._axleLastReadKey()) || "0", 10)
    const unread = msgs.filter(m => m.role === "assistant" && (m.at || 0) > lastRead).length
    this.axleCountTarget.textContent = String(unread)
    this.axleCountTarget.classList.toggle("hidden", unread <= 0)
  }

  _pushAxleMsg(role, content, formattedContent = null) {
    const list = this._axleMsgs()
    list.push({ role, content, formatted_content: formattedContent, at: Date.now() })
    localStorage.setItem(this._axleMsgsKey(), JSON.stringify(list.slice(-50)))
    this._renderAxleMessages()
  }

  _axleMsgs() {
    try {
      const raw = localStorage.getItem(this._axleMsgsKey())
      const arr = raw ? JSON.parse(raw) : []
      return Array.isArray(arr) ? arr : []
    } catch (_e) { return [] }
  }

  _renderAxleMessages() {
    if (!this.hasAxleMessagesTarget) return
    const list = this._axleMsgs()
    if (!list.length) {
      this.axleMessagesTarget.innerHTML = '<div class="axle-overlay-empty">Start chatting with Axle 💬</div>'
      return
    }
    this.axleMessagesTarget.innerHTML = list.map(m => {
      const body = m.role === "assistant"
        ? (m.formatted_content || this._escape(m.content))
        : this._escape(m.content)
      return `<div class="axle-overlay-msg axle-overlay-msg-${m.role}"><div>${body}</div></div>`
    }).join("")
    this.axleMessagesTarget.scrollTop = this.axleMessagesTarget.scrollHeight
  }

  _escape(str) { return (str || "").replace(/[&<>'"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;' }[c])) }

  _axleScope() {
    return this.hasUserIdValue && this.userIdValue ? `u${this.userIdValue}` : "anon"
  }

  _axleMsgsKey() {
    return `axleOverlayMsgs:${this._axleScope()}`
  }

  _axleLastReadKey() {
    return `axleLastReadAt:${this._axleScope()}`
  }

  _normalizeAssistantText(text) {
    const t = (text || "").trim()
    if (!t) return "I’m here. What do you want to tackle right now?"
    return t
  }

  _updateAxleViewport() {
    if (!window.visualViewport || !this.hasAxleOverlayTarget) return
    const vv = window.visualViewport
    this.axleOverlayTarget.style.top = `${vv.offsetTop || 0}px`
    this.axleOverlayTarget.style.height = `${vv.height}px`
  }

  _showSubmenu(index, anchorEl) {
    this._dismissSubmenu()
    const submenu = this.submenuTargets.find(s => s.dataset.submenuFor === index)
    if (!submenu) return
    this.activeSubmenuIndex = index
    submenu.classList.remove("hidden")

    const dockRect = this.element.querySelector(".app-dock").getBoundingClientRect()
    const anchorRect = anchorEl.getBoundingClientRect()
    const submenuRect = submenu.getBoundingClientRect()
    let left = anchorRect.left + (anchorRect.width / 2) - (submenuRect.width / 2)
    const pad = 8
    if (left < pad) left = pad
    if (left + submenuRect.width > window.innerWidth - pad) left = window.innerWidth - pad - submenuRect.width

    submenu.style.position = "fixed"
    submenu.style.bottom = (window.innerHeight - dockRect.top + 12) + "px"
    submenu.style.left = left + "px"

    const arrow = submenu.querySelector(".dock-submenu-arrow")
    if (arrow) arrow.style.left = (anchorRect.left + (anchorRect.width / 2) - left) + "px"
    requestAnimationFrame(() => submenu.classList.add("dock-submenu-visible"))
  }

  _dismissSubmenu() {
    this.submenuTargets.forEach(s => {
      s.classList.remove("dock-submenu-visible")
      s.classList.add("hidden")
      s.style.position = ""
      s.style.bottom = ""
      s.style.left = ""
    })
    this.activeSubmenuIndex = null
    this.element.querySelectorAll(".dock-submenu-active").forEach(el => el.classList.remove("dock-submenu-active"))
  }

  _handleOutsideClose(event) {
    if (this.hasStackTarget && this.stackTarget.contains(event.target)) return
    this.closeSecondary()
    this.closeAxleOverlay()
  }

  _lockTouchMove(event) {
    if (!this.hasAxleOverlayTarget || this.axleOverlayTarget.classList.contains("hidden")) return
    const scrollable = event.target.closest('.axle-overlay-messages')
    if (!scrollable) return event.preventDefault()
  }

  _lockPageScroll() {
    const y = window.scrollY || window.pageYOffset || 0
    this._lockedScrollY = y
    document.documentElement.classList.add("axle-overlay-lock")
    document.body.classList.add("axle-overlay-lock")
    document.body.style.position = "fixed"
    document.body.style.top = `-${y}px`
    document.body.style.left = "0"
    document.body.style.right = "0"
    document.body.style.width = "100%"
  }

  _unlockPageScroll() {
    document.documentElement.classList.remove("axle-overlay-lock")
    document.body.classList.remove("axle-overlay-lock")
    document.body.style.position = ""
    document.body.style.top = ""
    document.body.style.left = ""
    document.body.style.right = ""
    document.body.style.width = ""
    if (typeof this._lockedScrollY === "number") window.scrollTo(0, this._lockedScrollY)
  }

  _clearTimer() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer)
      this.longPressTimer = null
    }
    this.longPressTriggered = false
  }

  _highlightActive() {
    const path = window.location.pathname
    this.element.querySelectorAll(".dock-item[data-dock-path]").forEach(item => {
      const paths = item.dataset.dockPath?.split(",") || []
      const isActive = paths.some(p => {
        if (!p) return false
        if (p.endsWith("$")) return path === p.slice(0, -1)
        return path === p || (p !== "/" && path.startsWith(p))
      })
      item.classList.toggle("dock-active", isActive)
    })
  }

  _installLocationShareHook() {
    window.shareLocationAndGetRecommendations = this._shareLocationAndGetRecommendations.bind(this)
  }

  _removeLocationShareHook() {
    if (window.shareLocationAndGetRecommendations) {
      delete window.shareLocationAndGetRecommendations
    }
  }

  _removeLocationShareButtons() {
    if (!this.hasAxleMessagesTarget) return
    this.axleMessagesTarget.querySelectorAll('button[onclick*="shareLocationAndGetRecommendations"]').forEach((btn) => {
      const wrap = btn.closest('.mt-3') || btn.parentElement
      if (wrap) {
        wrap.remove()
      } else {
        btn.remove()
      }
    })
  }

  async _shareLocationAndGetRecommendations() {
    if (!navigator.geolocation) {
      this._pushAxleMsg("assistant", "Location services aren’t available on this device.")
      return
    }

    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const token = document.querySelector('meta[name="csrf-token"]')?.content
        const response = await fetch('/chat/update_location', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': token
          },
          body: JSON.stringify({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          })
        })

        const data = await response.json()
        if (response.ok && data.success) {
          this._removeLocationShareButtons()
          this._pushAxleMsg("assistant", `Got it — location updated to ${data.location || 'your current area'}.`)
          if (this.hasAxleInputTarget) {
            this.axleInputTarget.value = "I shared my location. Show me the best nearby service options and help me book one."
            this.sendAxleMessage()
          }
        } else {
          this._pushAxleMsg("assistant", data.error || "I couldn’t update your location. Try again.")
        }
      } catch (e) {
        this._pushAxleMsg("assistant", "I couldn’t update your location right now. Try again in a moment.")
      }
    }, () => {
      this._pushAxleMsg("assistant", "Location permission was denied. Enable it and try again.")
    }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 120000 })
  }
}
